import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, LogOut, ArrowLeft, Upload, X, Check, Clock, XCircle,
  RefreshCw, Plus, History, User, Home, FileText, Camera, Droplets, CreditCard, Bell, ArrowDownUp, ArrowDownLeft, ArrowUpRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { compressImage } from "@/utils/imageCompressor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount || 0);

function getCustomerAuthHeader() {
  const token = localStorage.getItem("customer_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user data was passed from AuthCallback
  const passedUser = location.state?.user;
  
  const [customer, setCustomer] = useState(passedUser || null);
  const [balance, setBalance] = useState(parseFloat(localStorage.getItem("customer_balance")) || 0);
  const [isLoading, setIsLoading] = useState(!passedUser);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpStep, setTopUpStep] = useState(1); // 1: input amount, 2: show transfer info
  const [topUpAmount, setTopUpAmount] = useState("");
  const [uniqueCode, setUniqueCode] = useState(0);
  const [transferAmount, setTransferAmount] = useState(0);
  const [bankInfo, setBankInfo] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [topUpRequests, setTopUpRequests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [minTopup, setMinTopup] = useState(10000);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [stripeLoading, setStripeLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const notifiedRef = useRef(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customer/notifications`, { headers: getCustomerAuthHeader() });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
      if (!notifiedRef.current && (res.data.unread_count || 0) > 0) {
        notifiedRef.current = true;
        const latest = (res.data.notifications || []).find((n) => !n.read);
        if (latest) toast.success(latest.title, { description: latest.message, duration: 6000 });
      }
    } catch (err) { /* ignore */ }
  }, []);

  const openNotifications = async () => {
    setShowNotif(true);
    if (unreadCount > 0) {
      try {
        await axios.post(`${API}/customer/notifications/read-all`, {}, { headers: getCustomerAuthHeader() });
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      } catch (err) { /* ignore */ }
    }
  };

  // Start a Stripe (demo) card top-up: creates a checkout session and redirects.
  const handleStripeTopUp = async () => {
    const amount = parseFloat(topUpAmount.replace(/\./g, ""));
    if (!amount || amount < minTopup || amount > 10000000) {
      toast.error(`Nominal top-up harus antara ${formatCurrency(minTopup)} - ${formatCurrency(10000000)}`);
      return;
    }
    setStripeLoading(true);
    try {
      const res = await axios.post(`${API}/customer/stripe-topup/checkout`, {
        amount, origin_url: window.location.origin
      }, { headers: getCustomerAuthHeader() });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memulai pembayaran Stripe");
      setStripeLoading(false);
    }
  };

  // On return from Stripe checkout, confirm payment and refresh balance.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_cancel")) {
      toast.info("Pembayaran dibatalkan");
      window.history.replaceState({}, "", "/pelanggan");
      return;
    }
    const sessionId = params.get("stripe_session_id");
    if (!sessionId) return;
    let active = true;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/customer/stripe-topup/status/${sessionId}`, {
          headers: getCustomerAuthHeader()
        });
        if (res.data.payment_status === "paid") {
          toast.success(`Saldo berhasil ditambahkan: ${formatCurrency(res.data.amount)}`);
          setBalance(res.data.balance);
          localStorage.setItem("customer_balance", res.data.balance);
          window.history.replaceState({}, "", "/pelanggan");
          return;
        }
      } catch (err) { /* keep polling */ }
      attempts += 1;
      if (active && attempts < 8) {
        setTimeout(poll, 2000);
      } else if (active) {
        toast.info("Pembayaran sedang diproses. Saldo akan diperbarui sebentar lagi.");
        window.history.replaceState({}, "", "/pelanggan");
      }
    };
    poll();
    return () => { active = false; };
  }, []);

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      // Skip if user data was passed from AuthCallback
      if (passedUser) {
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem("customer_token");
      if (!token) {
        navigate("/login", { replace: true });
        return;
      }

      try {
        const res = await axios.get(`${API}/auth/customer/me`, {
          headers: getCustomerAuthHeader()
        });
        setCustomer(res.data);
        setBalance(res.data.balance || 0);
        localStorage.setItem("customer_balance", res.data.balance || 0);
      } catch (err) {
        // Do NOT auto-clear tokens or redirect on error. User must click "Keluar" to log out.
        // Transient network / 401 errors previously kicked customer out of session on reopen.
        console.error("Auth check failed:", err);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate, passedUser]);

  // Fetch balance
  const fetchBalance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customer/balance`, {
        headers: getCustomerAuthHeader()
      });
      setBalance(res.data.balance);
      localStorage.setItem("customer_balance", res.data.balance);
    } catch (err) {
      console.error("Failed to fetch balance:", err);
    }
  }, []);

  // Fetch top-up requests
  const fetchTopUpRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customer/topup-requests`, {
        headers: getCustomerAuthHeader()
      });
      setTopUpRequests(res.data.requests || []);
    } catch (err) {
      console.error("Failed to fetch top-up requests:", err);
    }
  }, []);

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customer/transactions?limit=20`, {
        headers: getCustomerAuthHeader()
      });
      setTransactions(res.data.transactions || []);
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
    }
  }, []);

  const fetchBalanceHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customer/balance-history`, { headers: getCustomerAuthHeader() });
      setBalanceHistory(res.data.history || []);
    } catch (err) { /* ignore */ }
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (!isLoading && customer) {
      fetchBalance();
      fetchTopUpRequests();
      fetchTransactions();
      fetchNotifications();
      fetchBalanceHistory();
    }
  }, [isLoading, customer, fetchBalance, fetchTopUpRequests, fetchTransactions, fetchNotifications, fetchBalanceHistory]);

  // Live refresh so auto top-ups show up without a manual reload
  useEffect(() => {
    if (isLoading || !customer) return;
    const iv = setInterval(() => {
      fetchBalance();
      fetchNotifications();
      fetchTopUpRequests();
      fetchBalanceHistory();
    }, 20000);
    return () => clearInterval(iv);
  }, [isLoading, customer, fetchBalance, fetchNotifications, fetchTopUpRequests, fetchBalanceHistory]);

  // Load minimum top-up configured by admin
  useEffect(() => {
    axios.get(`${API}/customer/topup-config`)
      .then((res) => setMinTopup(res.data.min_topup ?? 10000))
      .catch(() => {});
  }, []);

  // Handle logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/customer/logout`, {}, {
        headers: getCustomerAuthHeader()
      });
    } catch (err) {
      console.error("Logout error:", err);
    }
    
    // Clear local storage
    localStorage.removeItem("customer_token");
    localStorage.removeItem("customer_id");
    localStorage.removeItem("customer_name");
    localStorage.removeItem("customer_email");
    localStorage.removeItem("customer_picture");
    localStorage.removeItem("customer_balance");
    localStorage.removeItem("customer_role");
    
    toast.success("Berhasil logout");
    navigate("/", { replace: true });
  };

  // Handle file upload for payment proof
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }

    try {
      // Compress image before converting to base64
      const compressedBase64 = await compressImage(file, {
        maxWidth: 800,
        maxHeight: 800,
        quality: 0.7
      });
      setPaymentProof(compressedBase64);
      toast.success("Bukti pembayaran berhasil diupload");
    } catch (err) {
      toast.error("Gagal mengupload gambar");
    }
  };

  // Step 1: Generate unique code
  const handleGenerateCode = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(topUpAmount.replace(/\./g, ""));
    if (!amount || amount < minTopup || amount > 10000000) {
      toast.error(`Nominal top-up harus antara ${formatCurrency(minTopup)} - ${formatCurrency(10000000)}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/customer/generate-topup-code`, {
        amount
      }, {
        headers: getCustomerAuthHeader()
      });

      setUniqueCode(res.data.unique_code);
      setTransferAmount(res.data.transfer_amount);
      setBankInfo(res.data.bank_info);
      setTopUpStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal generate kode unik");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Confirm transfer and create request
  const handleConfirmTransfer = async () => {
    const amount = parseFloat(topUpAmount.replace(/\./g, ""));
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/customer/topup-with-unique-code`, {
        amount,
        unique_code: uniqueCode,
        transfer_amount: transferAmount
      }, {
        headers: getCustomerAuthHeader()
      });

      toast.success("Permintaan top-up berhasil! Silakan transfer sesuai nominal.");
      resetTopUpForm();
      fetchTopUpRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim permintaan top-up");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset top-up form
  const resetTopUpForm = () => {
    setShowTopUp(false);
    setTopUpStep(1);
    setTopUpAmount("");
    setUniqueCode(0);
    setTransferAmount(0);
    setBankInfo(null);
    setPaymentProof(null);
  };

  // Handle top-up submission (legacy - still works)
  const handleTopUpSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(topUpAmount.replace(/\./g, ""));
    if (!amount || amount < minTopup || amount > 10000000) {
      toast.error(`Nominal top-up harus antara ${formatCurrency(minTopup)} - ${formatCurrency(10000000)}`);
      return;
    }

    if (!paymentProof) {
      toast.error("Upload bukti pembayaran terlebih dahulu");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/customer/topup-request`, {
        amount,
        payment_proof: paymentProof
      }, {
        headers: getCustomerAuthHeader()
      });

      toast.success("Permintaan top-up berhasil dikirim!");
      resetTopUpForm();
      fetchTopUpRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim permintaan top-up");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format amount input with thousand separator
  const formatAmountInput = (value) => {
    const numericValue = value.replace(/\D/g, "");
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="spinner w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const customerName = customer?.name || localStorage.getItem("customer_name") || "Pelanggan";
  const customerEmail = customer?.email || localStorage.getItem("customer_email") || "";
  const customerPicture = customer?.picture || localStorage.getItem("customer_picture") || "";

  return (
    <div className="min-h-screen hero-gradient">
      {/* Header */}
      <header className="bg-gradient-to-r from-sky-600 to-blue-700 text-white">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
              <span className="text-xl font-heading font-semibold">Produk Digital</span>
            </Link>
            <div className="flex items-center gap-4">
              <button onClick={openNotifications} className="relative p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-testid="customer-notif-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="customer-logout-btn">
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Keluar</span>
              </button>
            </div>
          </div>
        </div>

        {/* Profile Section */}
        <div className="container mx-auto px-6 pb-6">
          <div className="flex items-center gap-4">
            {customerPicture ? (
              <img src={customerPicture} alt={customerName} className="w-14 h-14 rounded-full border-2 border-white/30" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-heading font-bold">{customerName}</h1>
              <p className="text-sky-100 text-sm">{customerEmail}</p>
              <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">Pelanggan</span>
            </div>
          </div>

          {/* Balance Card */}
          <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sky-100 text-sm">Saldo Anda</p>
                <p className="text-2xl font-heading font-bold" data-testid="customer-balance">{formatCurrency(balance)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={fetchBalance} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                  <RefreshCw className="w-5 h-5" />
                </button>
                <Button
                  onClick={() => setShowTopUp(true)}
                  className="bg-white text-sky-600 hover:bg-sky-50 gap-2"
                  data-testid="topup-btn"
                >
                  <Plus className="w-4 h-4" />
                  Top Up
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "dashboard" 
                  ? "border-sky-500 text-sky-600" 
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Home className="w-4 h-4 inline mr-2" />
              Beranda
            </button>
            <button
              onClick={() => setActiveTab("topup")}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "topup" 
                  ? "border-sky-500 text-sky-600" 
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Wallet className="w-4 h-4 inline mr-2" />
              Riwayat Top-Up
            </button>
            <button
              onClick={() => setActiveTab("saldo")}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "saldo" 
                  ? "border-sky-500 text-sky-600" 
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid="customer-tab-saldo"
            >
              <ArrowDownUp className="w-4 h-4 inline mr-2" />
              Riwayat Saldo
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "history" 
                  ? "border-sky-500 text-sky-600" 
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <History className="w-4 h-4 inline mr-2" />
              Riwayat Transaksi
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-6">
        {activeTab === "dashboard" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Quick Actions */}
            <div>
              <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">Layanan</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Link to="/pdam-digiflazz" className="bg-white rounded-xl p-4 border border-slate-100 hover:border-sky-200 hover:shadow-md transition-all text-center" data-testid="customer-pdam-digiflazz-btn">
                  <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mx-auto mb-2">
                    <Droplets className="w-6 h-6 text-sky-600" />
                  </div>
                  <p className="font-medium text-slate-800 text-sm">Tagihan PDAM</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">DigiFlazz</p>
                </Link>
                <button
                  onClick={() => setShowTopUp(true)}
                  className="bg-white rounded-xl p-4 border border-slate-100 hover:border-emerald-200 hover:shadow-md transition-all text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                    <Plus className="w-6 h-6 text-emerald-600" />
                  </div>
                  <p className="font-medium text-slate-800 text-sm">Top Up Saldo</p>
                </button>
              </div>
            </div>

            {/* Recent Top-Up Requests */}
            {topUpRequests.length > 0 && (
              <div>
                <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">Permintaan Top-Up Terbaru</h2>
                <div className="space-y-3">
                  {topUpRequests.slice(0, 3).map((req) => (
                    <div key={req.id} className="bg-white rounded-xl p-4 border border-slate-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-800">{formatCurrency(req.amount)}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(req.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}
                          </p>
                        </div>
                        <StatusBadge status={req.status} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "topup" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading font-semibold text-slate-800">Riwayat Top-Up</h2>
              <Button onClick={() => setShowTopUp(true)} size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                Top Up
              </Button>
            </div>
            
            {topUpRequests.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
                <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Belum ada permintaan top-up</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topUpRequests.map((req) => (
                  <div key={req.id} className="bg-white rounded-xl p-4 border border-slate-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-800">{formatCurrency(req.amount)}</p>
                        {req.transfer_amount && (
                          <p className="text-xs text-sky-600 font-medium">
                            Transfer: {formatCurrency(req.transfer_amount)}
                          </p>
                        )}
                        <p className="text-xs text-slate-500">
                          {new Date(req.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}
                        </p>
                        {req.note && <p className="text-xs text-slate-400 mt-1">Catatan: {req.note}</p>}
                      </div>
                      <StatusBadge status={req.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "saldo" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">Riwayat Saldo</h2>
            {balanceHistory.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
                <ArrowDownUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Belum ada pergerakan saldo</p>
              </div>
            ) : (
              <div className="space-y-3">
                {balanceHistory.map((h) => {
                  const isIn = (h.amount ?? 0) >= 0;
                  return (
                    <div key={h.id} className="bg-white rounded-xl p-4 border border-slate-100 flex items-center justify-between" data-testid="balance-history-item">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isIn ? "bg-emerald-100" : "bg-red-100"}`}>
                          {isIn ? <ArrowDownLeft className="w-5 h-5 text-emerald-600" /> : <ArrowUpRight className="w-5 h-5 text-red-500" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{h.description || (isIn ? "Saldo masuk" : "Saldo keluar")}</p>
                          <p className="text-xs text-slate-400">{new Date(h.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-bold ${isIn ? "text-emerald-600" : "text-red-500"}`}>{isIn ? "+" : "-"}{formatCurrency(Math.abs(h.amount || 0))}</p>
                        {h.balance_after != null && <p className="text-[11px] text-slate-400">Saldo: {formatCurrency(h.balance_after)}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "history" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">Riwayat Transaksi</h2>
            
            {transactions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
                <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Belum ada transaksi</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((txn) => (
                  <div key={txn.id} className="bg-white rounded-xl p-4 border border-slate-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{txn.product_name || txn.service_type}</p>
                        <p className="text-xs text-slate-500">{txn.customer_no}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(txn.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-800">{formatCurrency(txn.total)}</p>
                        <span className="text-xs text-emerald-600">Berhasil</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </main>

      {/* Top-Up Modal */}
      <AnimatePresence>
        {showTopUp && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={resetTopUpForm}>
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.95 }}
              transition={{ type: "spring", damping: 25 }}
              className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-bold text-slate-800">
                    {topUpStep === 1 ? "Top Up Saldo" : "Transfer Pembayaran"}
                  </h2>
                  <button onClick={resetTopUpForm} className="p-2 rounded-full hover:bg-slate-100">
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>

                {topUpStep === 1 ? (
                  /* Step 1: Input Amount */
                  <form onSubmit={handleGenerateCode} className="space-y-4">
                    {/* Amount Input */}
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1.5">Nominal Top-Up</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">Rp</span>
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="50.000"
                          value={topUpAmount}
                          onChange={(e) => setTopUpAmount(formatAmountInput(e.target.value))}
                          className="h-12 pl-12 text-lg"
                          data-testid="topup-amount-input"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Min: {formatCurrency(minTopup)} - Max: {formatCurrency(10000000)}</p>
                    </div>

                    {/* Quick Amount Buttons */}
                    <div className="flex flex-wrap gap-2">
                      {[50000, 100000, 200000, 500000, 1000000].map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => setTopUpAmount(formatAmountInput(amount.toString()))}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-sky-100 hover:text-sky-700 rounded-full text-sm font-medium transition-colors"
                        >
                          {formatCurrency(amount)}
                        </button>
                      ))}
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting || !topUpAmount}
                      className="w-full h-12 rounded-xl font-semibold"
                      data-testid="generate-code-btn"
                    >
                      {isSubmitting ? (
                        <div className="spinner w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Lanjutkan"
                      )}
                    </Button>

                    {/* Divider */}
                    <div className="relative py-1">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-slate-200" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-2 text-xs text-slate-400">atau</span>
                      </div>
                    </div>

                    {/* Stripe (demo) card top-up */}
                    <Button
                      type="button"
                      onClick={handleStripeTopUp}
                      disabled={stripeLoading || !topUpAmount}
                      variant="outline"
                      className="w-full h-12 rounded-xl font-semibold border-sky-300 text-sky-700 hover:bg-sky-50"
                      data-testid="stripe-topup-btn"
                    >
                      {stripeLoading ? (
                        <div className="spinner w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Bayar Otomatis via Kartu (Demo)
                        </>
                      )}
                    </Button>
                    <p className="text-[11px] text-center text-slate-400">
                      Pembayaran kartu memakai Stripe mode percobaan (bukan uang nyata).
                    </p>
                  </form>
                ) : (
                  /* Step 2: Transfer Info with Unique Code */
                  <div className="space-y-4">
                    {/* Transfer Instructions */}
                    <div className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl p-5 text-white">
                      <p className="text-sm opacity-90 mb-1">Transfer tepat sebesar:</p>
                      <p className="text-3xl font-bold font-heading mb-3">
                        {formatCurrency(transferAmount)}
                      </p>
                      <div className="bg-white/20 rounded-lg p-3 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="opacity-80">Nominal top-up:</span>
                          <span className="font-medium">{formatCurrency(parseFloat(topUpAmount.replace(/\./g, "")))}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="opacity-80">Kode unik:</span>
                          <span className="font-medium text-yellow-300">+{uniqueCode}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bank Info */}
                    {bankInfo && (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                        <p className="text-sm font-medium text-slate-800">Transfer ke rekening:</p>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-600">Bank</span>
                            <span className="text-sm font-semibold text-slate-800">{bankInfo.bank_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-600">No. Rekening</span>
                            <span className="text-sm font-mono font-semibold text-slate-800">{bankInfo.account_number}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-600">Atas Nama</span>
                            <span className="text-sm font-semibold text-slate-800">{bankInfo.account_holder}</span>
                          </div>
                        </div>
                        {/* Copy Button */}
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(bankInfo.account_number);
                            toast.success("Nomor rekening disalin!");
                          }}
                          className="w-full py-2 bg-slate-200 hover:bg-slate-300 rounded-lg text-sm font-medium text-slate-700 transition-colors"
                        >
                          Salin Nomor Rekening
                        </button>
                      </div>
                    )}

                    {/* Warning */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <p className="text-xs text-amber-800">
                        <strong>Penting:</strong> Transfer dengan nominal <strong>TEPAT</strong> seperti di atas agar pembayaran dapat diverifikasi. Kode unik tidak akan dimasukkan ke saldo Anda.
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setTopUpStep(1)}
                        className="flex-1 h-12 rounded-xl"
                      >
                        Kembali
                      </Button>
                      <Button
                        type="button"
                        onClick={handleConfirmTransfer}
                        disabled={isSubmitting}
                        className="flex-1 h-12 rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-700"
                        data-testid="confirm-transfer-btn"
                      >
                        {isSubmitting ? (
                          <div className="spinner w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          "Sudah Transfer"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Notifications Panel */}
      <AnimatePresence>
        {showNotif && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end p-4" onClick={() => setShowNotif(false)}>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 40 }}
              className="bg-white rounded-2xl w-full max-w-sm mt-14 max-h-[70vh] overflow-y-auto shadow-xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="customer-notif-panel"
            >
              <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-800">Notifikasi</h3>
                <button onClick={() => setShowNotif(false)} className="p-1.5 rounded-full hover:bg-slate-100">
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Bell className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  Belum ada notifikasi
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {notifications.map((n) => (
                    <div key={n.id} className={`p-4 ${!n.read ? "bg-sky-50/60" : ""}`}>
                      <p className="font-medium text-slate-800 text-sm">{n.title}</p>
                      <p className="text-sm text-slate-600 mt-0.5">{n.message}</p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {new Date(n.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const config = {
    pending: { icon: Clock, text: "Menunggu", className: "bg-amber-100 text-amber-700" },
    approved: { icon: Check, text: "Disetujui", className: "bg-emerald-100 text-emerald-700" },
    rejected: { icon: XCircle, text: "Ditolak", className: "bg-red-100 text-red-700" }
  };

  const { icon: Icon, text, className } = config[status] || config.pending;

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3.5 h-3.5" />
      {text}
    </span>
  );
}
